<template>
  <Transition name="consult-chip-fade">
    <div
      v-if="visible"
      class="consult-chip"
      role="status"
    >
      <p class="consult-chip__text">{{ hintText }}</p>
      <div class="consult-chip__actions">
        <a
          class="consult-chip__link"
          :href="mailtoHref"
        >{{ t('contact.emailLabel') }}</a>
        <a
          v-if="booking"
          class="consult-chip__link"
          :href="booking"
          target="_blank"
          rel="noopener noreferrer"
        >{{ t('contact.bookingLabel') }}</a>
        <button
          type="button"
          class="consult-chip__dismiss"
          @click="dismiss"
        >
          {{ t('consult.dismiss') }}
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { calendlyUrl } from '@/config/contactUrls';

const { t } = useI18n();
const router = useRouter();

const visible = ref(false);
const hintIndex = ref(0);
const booking = computed(() => calendlyUrl());
const mailtoHref = computed(() => `mailto:${t('contact.email')}`);

const hintKeys = ['consult.chipHint1', 'consult.chipHint2', 'consult.chipHint3'];
const hintText = computed(() => t(hintKeys[hintIndex.value]));

const LS_DISMISS = 'growboard_consult_dismissed_at';
const SS_STATE = 'growboard_consult_session_state';
const SS_NAV = 'growboard_consult_nav_count';
const COOLDOWN_MS = 12 * 24 * 60 * 60 * 1000;
const MIN_NAV = 6;
const MIN_MS = 120 * 1000;
const appStarted = Date.now();

function isCooldownActive() {
  const raw = localStorage.getItem(LS_DISMISS);
  if (!raw) return false;
  const ts = parseInt(raw, 10);
  return !Number.isNaN(ts) && Date.now() - ts < COOLDOWN_MS;
}

function dismiss() {
  localStorage.setItem(LS_DISMISS, String(Date.now()));
  visible.value = false;
  sessionStorage.setItem(SS_STATE, 'dismissed');
}

function tryShowOnNav() {
  if (visible.value) return;
  if (sessionStorage.getItem(SS_STATE) === 'dismissed') return;
  if (sessionStorage.getItem(SS_STATE) === 'shown') return;
  if (isCooldownActive()) return;
  const n = parseInt(sessionStorage.getItem(SS_NAV) || '0', 10) + 1;
  sessionStorage.setItem(SS_NAV, String(n));
  if (n < MIN_NAV) return;
  if (Date.now() - appStarted < MIN_MS) return;
  hintIndex.value = Math.floor(Math.random() * hintKeys.length);
  sessionStorage.setItem(SS_STATE, 'shown');
  visible.value = true;
}

let removeAfterEach;

onMounted(() => {
  removeAfterEach = router.afterEach(() => {
    tryShowOnNav();
  });
});

onUnmounted(() => {
  if (typeof removeAfterEach === 'function') {
    removeAfterEach();
  }
});
</script>

<style scoped>
.consult-chip-fade-enter-active,
.consult-chip-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.consult-chip-fade-enter-from,
.consult-chip-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 6px);
}

.consult-chip {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1005;
  max-width: min(520px, calc(100vw - 24px));
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(102, 126, 234, 0.25);
  font-size: 14px;
  line-height: 1.45;
  color: #333;
}

.consult-chip__text {
  margin: 0 0 10px;
}

.consult-chip__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 14px;
}

.consult-chip__link {
  color: #5a67d8;
  font-weight: 500;
  text-decoration: none;
}
.consult-chip__link:hover {
  text-decoration: underline;
}

.consult-chip__dismiss {
  margin-left: auto;
  padding: 6px 12px;
  font-size: 13px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fafafa;
  cursor: pointer;
  color: #555;
}
.consult-chip__dismiss:hover {
  background: #f0f0f0;
}

@media (max-width: 768px) {
  .consult-chip {
    bottom: 14px;
    max-width: calc(100vw - 20px);
  }
  .consult-chip__dismiss {
    margin-left: 0;
    width: 100%;
  }
}
</style>
